<template>
<div class="other-times">
    <div class="label f8" v-if="showNow && otherTimes.length > 0">{{ $t("Other Times") }}</div>
    <div class="label f8" v-else-if="otherTimes.length > 0">{{ $t("Times") }}</div>
    <div class="label f8" v-else-if="otherTimes.length === 0">{{ $t("No More Available Times Today") }}</div>
    <div class="othertimesbody">
      <div class="leftarrow" :class="{'-show': showLeftArrow}" @click="leftArrowAction">
        <UIIcon name="chevron-left" />
      </div>
      <div class="othertimesgrid">
        <div
          class="othertime"
          v-for="time in otherTimes"
          :key="time"
          :class="{ '-conflict': conflictTimes.includes(time) }"
        >
          <UIButton
            :ghost="true"
            class="othertime"
            :class="{'-active': otherTimeActive(time)}"
            @click="$emit('setOtherTime', time)"
          >{{ prettyTime(time) }}</UIButton>
          <span class="conflictmessage f7">{{ $t('Conflict') }}</span>
        </div>
      </div>
      <div class="rightarrow" :class="{'-show': showRightArrow}" @click="rightArrowAction">
        <UIIcon name="chevron-right" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';

import { isEqual } from 'lodash-es';

import moment from 'moment-timezone';
import DateHelper from '@/core/shared/helpers/DateHelper';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIIcon from '@/core/shared/components/ui/UIIcon';

@Component({
  components: {
    UIButton,
    UIIcon,
  },
  props: {
    conflictTimes: {
      type: Array,
      required: true,
    },
    otherTimes: {
      type: Array,
      required: true,
    },
    otherTimesNumber: {
      type: Number,
      required: true,
    },
    currentTime: {
      type: String,
      required: true,
    },
    booking: {
      type: Object,
      required: true,
    },
    showNow: {
      type: Boolean,
      required: true,
    },
    showCustom: {
      type: Boolean,
      required: true,
    },
    startTime: {
      type: String,
      required: true,
    },
    twentyFourHourTime: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    startTime (newVal, oldVal) {
      if (!DateHelper.dateMatch(newVal, oldVal)) {
        this.roundUpTime(newVal);
      }
    },
  },
})
export default class StartTimeOtherTimes extends Vue {
  increment = 30;
  startingTime = null;

  get showLeftArrow () {
    // convert increment to milliseconds, and only show left arrow if we still have that to go.
    if (DateHelper.dateMatch(this.startingTime, this.currentTime)) {
      return moment(this.startingTime).diff(moment(this.currentTime)) > (this.increment * 60 * 1000 + 1000);
    } else {
      const todayMidnight = moment(this.startingTime).hour(0).minute(0).second(0);
      return moment(this.otherTimes[0]).diff(todayMidnight) >= (this.increment * 60 * 1000 + 1000);
    }
  }

  get showRightArrow () {
    const tomorrowMidnight = moment(this.startingTime).add(1, 'day').hour(0).minute(0).second(0);
    if (!this.otherTimes[this.otherTimesNumber - 1]) return false;
    const modifier = this.otherTimes.length - 1;
    return tomorrowMidnight.diff(moment(this.otherTimes[modifier])) > (this.increment * 60 * 1000 + (modifier * 1000));
  }

  onStartTimeChanged (newValue, oldValue) {
    if (!DateHelper.dateMatch(newValue, oldValue)) this.roundUpTime(newValue);
  }

  mounted () {
    this.roundUpTime(this.startTime);
  }

  roundUpTime (time) {
    const roundedUp = Math.ceil(moment(time).minute() / this.increment) * this.increment;
    this.startingTime = moment(time).minute(roundedUp).second(0).format();

    this.generateOtherTimes();
  }

  generateOtherTimes () {
    const times = [];
    for (var i = 0; i < this.otherTimesNumber; i++) {
      const timeToAdd = moment(this.startingTime).add((i * this.increment), 'minutes').format();
      if (DateHelper.dateMatch(timeToAdd, this.startTime)) {
        times.push(timeToAdd);
      }
    }

    this.$emit('update:otherTimes', times);
  }

  isEqual (date1, date2) {
    if (date1 && date2) {
      return moment(date1).isSame(date2);
    }
  }

  prettyTime (date) {
    if (this.twentyFourHourTime) {
      return moment(date).format('H:mm');
    } else {
      return moment(date).format('h:mm a');
    }
  }

  leftArrowAction () {
    const potentialStartingTime = moment(this.startingTime).subtract(this.otherTimesNumber * this.increment, 'minutes').format();
    if (DateHelper.dateMatch(potentialStartingTime, this.startTime)) {
      this.startingTime = potentialStartingTime;
    } else {
      this.startingTime = moment(this.startTime).hour(0).minute(0).second(0);
    }
    if (!this.showLeftArrow) this.roundUpTime(this.currentTime);
    this.generateOtherTimes();
  }

  rightArrowAction () {
    this.startingTime = moment(this.startingTime).add(this.otherTimesNumber * this.increment, 'minutes').format();
    this.generateOtherTimes();
  }

  otherTimeActive (time) {
    return isEqual(time, this.startTime) && !this.showCustom && !this.booking._startNow;
  }
}
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.other-times {
  width: 100%;
  padding: 0 2.5rem;

  @media (--tablet) {
    padding: 0;
  }

  & .label {
    margin-bottom: var(--spacingXS);
  }

  & .conflictmessage {
    display: block;
    opacity: 0;
    padding-top: 0.2rem;
    color: var(--colorErrorFlat);
    transition: opacity 0.1s ease;
    text-align: center;
  }

  & .-conflict {
    & .conflictmessage {
      opacity: 1;
      font-style: italic;
    }
  }

  @media (--tablet) {
    width: auto;
  }

  & .othertimesbody {
    position: relative;
    display: grid;
    min-height: 14rem;

    & .leftarrow,
    & .rightarrow {
      position: relative;
      top: 5.5rem;
      flex: 0 1 auto;
      display: block;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
      cursor: pointer;

      &.-show {
        display: block;
        opacity: 0.5;
        pointer-events: all;

        &:hover {
          opacity: 1;
        }
      }

      & svg {
        position: relative;
        top: -0.5rem;
        width: 3rem;
        height: 3rem;
      }
    }

    & .rightarrow {
      position: absolute;
      right: -3rem;
    }

    & .leftarrow {
      position: absolute;
      left: -3rem;
    }

    & .othertimesgrid {
      display: grid;
      grid-gap: var(--spacingSm);
      grid-template-columns: repeat(3, 1fr);
      width: 100%;

      @media (--tablet) {
        grid-template-columns: repeat(5, 1fr);
      }

      & .othertime {
        & button {
          font-size: 1.2rem;
          padding: calc(var(--spacingSm) - 0.3rem);
          width: 100%;
          min-width: 6.5rem;

          &.-active {
            color: var(--colorWhite);
          }
        }
      }
    }
  }
}

</style>
